import styles from './index.module.css'

import clsx from 'clsx'
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  clientLibraryDiscoveryPanelFavorite,
  clientLibraryDiscoveryPanelRequestAccess,
} from '@disco/state-management/actions/client-library-discovery-panel-actions'
import { getConstanceSetting } from '@disco/state-management/selectors/user'
import { getTextColor } from '@disco/utils/colors'

import type {
  ClientLibraryDiscoveryPanelItem,
  ClientLibraryId,
} from 'types/client-library'
import type { PopoverItemType } from 'types/ui'

import LibraryArtwork from 'components/common/library-artwork'
import Button from 'components/ui/button'
import EllipsisMenu from 'components/ui/ellipsis-menu'
import GridItem from 'components/ui/grid-item'
import Icon from 'components/ui/icon'
import Lozenge from 'components/ui/lozenge'
import Tooltip from 'components/ui/tooltip'

import { useDispatch, useSelector } from 'hooks/use-redux'

const getLozengeColor = (
  status: ClientLibraryDiscoveryPanelItem['status'] | null | undefined,
) => {
  switch (status) {
    case 'approved':
      return 'bold-green'

    case 'invited':
    case 'pending':
      return 'bold-yellow'

    case 'rejected':
      return 'bold-red'

    default:
      return undefined
  }
}

const getStatusMessage = (
  status: ClientLibraryDiscoveryPanelItem['status'] | null | undefined,
): string | null | undefined => {
  switch (status) {
    case 'invited':
    case 'pending':
      return 'Awaiting access'

    case 'rejected':
      return 'Rejected'

    default:
      return undefined
  }
}

const getIcons = (lib: ClientLibraryDiscoveryPanelItem) => {
  const icons: React.ReactNode[] = []

  if (lib.status === 'approved') {
    icons.push(
      <Tooltip content="You have download access to this Catalog.">
        <Icon color="#5f63ee" type="checkbox" />
      </Tooltip>,
    )
  }

  if (lib.is_favorite) {
    icons.push(
      <Tooltip content="You have favorited this Catalog.">
        <Icon color="#5f63ee" type="star-in-box" />
      </Tooltip>,
    )
  }

  return icons
}

type Props = {
  lib: ClientLibraryDiscoveryPanelItem
  onClick?: (id: ClientLibraryId) => void
  type?: 'shadow'
  featured?: boolean
}

/**
 * Grid item component for displaying library/catalog information with access status and actions
 * @param lib - Library discovery panel item data
 * @param onClick - Click handler for the grid item
 * @param type - Visual style type, currently supports 'shadow'
 * @param featured - Whether this is a featured library with enhanced display
 */
const LibraryGridItem = ({
  lib,
  onClick,
  type,
  featured,
}: Props): null | React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  // Feature flag for Discover Page Rebrand
  const discoverPageRebrand2026 = useSelector(state =>
    getConstanceSetting(state, 'DISCOVER-PAGE-REBRAND-2026'),
  )

  const id = lib.id
  const image = lib.discovery_image_url || lib.image_logo_url
  const display_name = lib.display_name
  const status = lib.status
  const short_description = lib.short_description
  const publicUrl = lib.public_url

  // Local state for optimistic UI updates when toggling favorites
  const [isFavorite, setIsFavorite] = useState(lib.is_favorite)

  // Sync local state with prop when it changes (e.g., after API response)
  useEffect(() => {
    setIsFavorite(lib.is_favorite)
  }, [lib.is_favorite])

  // Navigate to catalog inner page with data passed via state
  const handleCatalogClick = useCallback(() => {
    if (discoverPageRebrand2026) {
      navigate(`/discover/catalog?id=${id}`, {
        state: { catalog: lib },
      })
    }
    if (onClick) {
      onClick(id)
    }
  }, [discoverPageRebrand2026, navigate, id, lib, onClick])
  const favorite = useCallback(() => {
    setIsFavorite(prev => !prev) // Optimistic update
    dispatch(clientLibraryDiscoveryPanelFavorite(id, !isFavorite))
  }, [dispatch, id, isFavorite])
  const requestAccess = useCallback(() => {
    dispatch(clientLibraryDiscoveryPanelRequestAccess(id, status))
  }, [dispatch, id, status])
  let options: PopoverItemType[] = [
    {
      label: isFavorite ? 'Unfavorite Catalog' : 'Favorite Catalog',
      onClick: favorite,
    },
  ]

  if (status === null || status === 'rejected') {
    options.unshift({
      label: 'Request access',
      onClick: requestAccess,
    })
  }

  const handleFavoriteClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      favorite()
    },
    [favorite],
  )

  if (!lib) return null

  // Fallback values for redesigned view
  const catalogName = display_name || 'Untitled Catalog' // Fallback catalog name if display_name is missing
  const description = short_description

  // Redesigned overlay - icons on artwork
  const redesignedOverlay: React.ReactNode = (
    <GridItem.Overlay
      actions={
        <div className={styles.libraryGridItemArtworkIcons}>
          {status === 'approved' && (
            <Tooltip content="You have download access to this Catalog.">
              <div className={styles.libraryGridItemArtworkIcon}>
                <Icon color="#fff" type="check" width={15} height={15} />
              </div>
            </Tooltip>
          )}
        </div>
      }
    />
  )

  // Original overlay with ellipsis menu
  const originalOverlay: React.ReactNode = (
    <GridItem.Overlay
      actions={
        <EllipsisMenu
          compact
          icon="ellipsis"
          options={options}
          hasArrow={false}
        />
      }
      info={
        short_description &&
        !featured && (
          <Tooltip position="top" content={short_description}>
            <Button appearance="icon" icon="info" />
          </Tooltip>
        )
      }
    />
  )

  const statusMessage = getStatusMessage(status)
  const media: React.ReactNode = (
    <LibraryArtwork
      color={getTextColor(display_name)}
      image={image}
      overlay={discoverPageRebrand2026 ? redesignedOverlay : originalOverlay}
      status={
        !discoverPageRebrand2026 &&
        statusMessage && (
          <Lozenge color={getLozengeColor(status)} text={statusMessage} />
        )
      }
      ratio={discoverPageRebrand2026 ? 1 : featured ? 1.8 : 1}
      type={type}
      noPadding={discoverPageRebrand2026}
    />
  )

  // Redesigned title content: Catalog name, Description
  const redesignedTitle = (
    <div className={styles.libraryGridItemContent}>
      <div className={styles.libraryGridItemTitle}>{catalogName}</div>
      <div className={styles.libraryGridItemDescription}>{description}</div>
    </div>
  )

  // Original title content
  const originalTitle = (
    <div
      className={clsx(styles.libraryGridItemTitleContainer, {
        [styles.libraryGridItemTitleContainerH600]: featured,
      })}
    >
      <div className="overflow-ellipsis">{display_name}</div>
      <div className={styles.libraryGridItemTitleIconsContainer}>
        {getIcons(lib)}
      </div>
    </div>
  )

  // Favorite heart button for redesigned view
  const favoriteButton = discoverPageRebrand2026 && (
    <div className={styles.libraryGridItemFavoriteContainer}>
      <button
        type="button"
        className={styles.libraryGridItemFavoriteButton}
        onClick={handleFavoriteClick}
        aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
      >
        <Icon
          type="heart"
          width={20}
          height={20}
          className={clsx(styles.libraryGridItemFavoriteIcon, {
            [styles.libraryGridItemFavoriteIconFilled]: isFavorite,
            [styles.libraryGridItemFavoriteIconNotFilled]: !isFavorite,
          })}
        />
      </button>
    </div>
  )

  const gridItemLink = discoverPageRebrand2026
    ? undefined
    : {
        external: true,
        href: `/api-auth/transfer-start/?client_library_id=${id}`,
      }

  return (
    <div
      className={clsx(styles.libraryGridItemWrapper, {
        [styles.libraryGridItemRedesigned]: discoverPageRebrand2026,
      })}
    >
      <GridItem
        link={gridItemLink}
        media={media}
        title={discoverPageRebrand2026 ? redesignedTitle : originalTitle}
        description={
          !discoverPageRebrand2026 && featured ? short_description : undefined
        }
        onClick={handleCatalogClick}
      />
      {favoriteButton}
    </div>
  )
}

export default LibraryGridItem
